import { useAtom } from "jotai";
import { useContext } from "react";
import { toast } from "react-toastify";
import dataService from "../../../../../helpers/dataService";
import tools from "../../../../../helpers/tools";
import { deleteComment, patchComment, postComment } from "../../../../../services/commentService";
import { getCompanyFiles } from "../../../../../services/fileService";
import { deleteTask, patchTask, patchTasksOrder, postTask } from "../../../../../services/taskService";
import { LeadPageContext } from "../../useLeads";
import { LeadContext } from "../LeadContext";
import { isFilesLoadedAtom, isLeadLoadingAtom, isSearchingFilesAtom, leadAtom } from "./atoms/leadAtoms";
import { ENTITY_REF_PAR_CATEGORIE } from "./profilLead";

// Données et actions d'un lead entreprise (PRO) ou DOMI : tâches, commentaires,
// assignation, statut, fichiers du volet « Infos et documents », devis déposés.
const useLeadItem = () => {
  const { productCategory } = useContext(LeadPageContext);
  const [lead, setLead] = useAtom(leadAtom);
  const [isLeadLoading, setIsLeadLoading] = useAtom(isLeadLoadingAtom);
  const { managers, contacts } = useContext(LeadContext);
  const [isSearchingFiles, setIsSearchingFiles] = useAtom(isSearchingFilesAtom);
  const [isFilesLoaded, setIsFilesLoaded] = useAtom(isFilesLoadedAtom);

  const initLead = (data) => {
    setLead({ ...data, files: [] });
    setIsFilesLoaded(false);
  };

  const getFormattedLeadTasks = (rawLead) => ({
    ...rawLead,
    tasks: tools.populateNamesFromId(managers, rawLead.tasks),
  });
  const getFormattedLeadComments = (rawLead) => ({
    ...rawLead,
    comments: tools.populateNamesFromId(contacts, rawLead.comments),
  });

  // Fichiers de la société et du produit, chargés une fois par lead, à
  // l'ouverture de « Infos et documents ».
  const chargerFichiers = async () => {
    if (isFilesLoaded || isSearchingFiles) return;
    setIsSearchingFiles(true);
    try {
      let companyFiles = [];
      if (lead.client) {
        companyFiles = await getCompanyFiles(lead.client.id);
      }
      let otherFiles = [];
      if (lead.productId === "MRP" && lead.isFinalized) {
        const MRPFiles = await getLeadMRPFiles();
        otherFiles = MRPFiles.map((f) => [f]);
      }
      if (lead.productId === "PJ") {
        const PJFiles = await getLeadPJFiles();
        otherFiles = PJFiles.map((f) => [f]);
      }
      if (lead.productId === "domi") {
        otherFiles = await getLeadDOMIFiles();
      }
      setLead((l) => ({ ...l, files: [...(l.files || []), ...companyFiles, ...otherFiles] }));
      setIsFilesLoaded(true);
    } finally {
      setIsSearchingFiles(false);
    }
  };

  // Devis déposé depuis la carte : il rejoint les fichiers du lead.
  const ajouterMedia = (media) => {
    setLead((l) => ({ ...l, medias: [...(Array.isArray(l.medias) ? l.medias : []), media] }));
  };

  const onUpdateTasks = (tasks) => {
    const oldLead = { ...lead };
    setLead({ ...lead, tasks });
    setIsLeadLoading(true);
    patchTasksOrder(lead.tasksId, { tasks })
      .then((data) => {
        toast.success("Tâches réordonnées");
        setLead(getFormattedLeadTasks({ ...lead, tasks: data.tasks }));
      })
      .catch(() => setLead(oldLead))
      .finally(() => setIsLeadLoading(false));
  };
  const onUpdateTask = (task) => {
    // PATCH /tasks/:id — body { taskId: VOIR CONSTANTS LEAD_TASKS, text: "" }
    const oldLead = { ...lead };
    setLead({
      ...lead,
      tasks: [...lead.tasks.map((t) => (t.id === task.id ? task : t))],
    });
    setIsLeadLoading(true);
    patchTask(task.id, task)
      .then((data) => {
        toast.success("Tâche mise à jour");
        const newLead = getFormattedLeadTasks({ ...lead, tasks: data.tasks });
        setLead(newLead);

        // La première tâche faite passe automatiquement un lead « à traiter »
        // en cours de traitement.
        if (task.isDone && lead.status === "TO_DO") {
          const completedTasks = data.tasks.filter((t) => t.isDone);
          if (completedTasks.length === 1) {
            onUpdateStatus("IN_PROGRESS", {}, newLead);
          }
        }
      })
      .catch(() => setLead(oldLead))
      .finally(() => setIsLeadLoading(false));
  };
  const onAddTask = (task) => {
    // POST /tasks — body { entity, entityRef, taskId, text }
    const oldLead = { ...lead };
    setIsLeadLoading(true);
    postTask({
      entity: lead.id,
      entityRef: ENTITY_REF_PAR_CATEGORIE[productCategory],
      ...task,
    })
      .then((data) => {
        toast.success("Tâche ajoutée");
        setLead(getFormattedLeadTasks({ ...lead, tasks: data.tasks }));
      })
      .catch(() => setLead(oldLead))
      .finally(() => setIsLeadLoading(false));
  };
  const onDeleteTask = (taskId) => {
    const oldLead = { ...lead };
    setIsLeadLoading(true);
    deleteTask(taskId)
      .then(() => {
        setLead({
          ...lead,
          tasks: [...lead.tasks.filter((t) => t.id !== taskId)],
        });
        toast.success("Tâche supprimée");
      })
      .catch(() => setLead(oldLead))
      .finally(() => setIsLeadLoading(false));
  };

  const onUpdateComments = (comments) => {
    setLead({ ...lead, comments });
  };
  const onUpdateComment = (comment) => {
    const oldLead = { ...lead };
    setLead({
      ...lead,
      comments: [...lead.comments.map((c) => (c.id === comment.id ? comment : c))],
    });
    setIsLeadLoading(true);
    patchComment(comment.id, comment)
      .then((data) => {
        toast.success("Commentaire mis à jour");
        setLead(getFormattedLeadComments({ ...lead, comments: data.comments }));
      })
      .catch(() => setLead(oldLead))
      .finally(() => setIsLeadLoading(false));
  };
  const onAddComment = (comment) => {
    const oldLead = { ...lead };
    setIsLeadLoading(true);
    postComment({
      entity: lead.id,
      entityRef: ENTITY_REF_PAR_CATEGORIE[productCategory],
      ...comment,
    })
      .then((data) => {
        toast.success("Commentaire ajouté");
        setLead((l) => getFormattedLeadComments({ ...l, comments: data.comments }));
      })
      .catch(() => setLead(oldLead))
      .finally(() => setIsLeadLoading(false));
  };
  const onDeleteComment = (commentId) => {
    const oldLead = { ...lead };
    setLead({
      ...lead,
      comments: [...lead.comments.filter((c) => c.id !== commentId)],
    });
    setIsLeadLoading(true);
    deleteComment(commentId)
      .then(() => toast.success("Commentaire supprimé"))
      .catch(() => setLead(oldLead))
      .finally(() => setIsLeadLoading(false));
  };

  // Assignation. La carte confirme déjà une réassignation (isForce) ; sans
  // forçage, l'API signale le gestionnaire en place et on demande alors.
  const onUpdateAssignTo = (assignedTo, isForce = false) => {
    setIsLeadLoading(true);
    const oldLead = { ...lead };
    setLead((l) => ({ ...l, assignedTo }));

    const urlByProductCategory = {
      PRO: `leads/${lead.id}/assigned-to`,
      DOMI: `quotes/${lead.id}/assigned-to`,
    };

    dataService.patch(
      urlByProductCategory[productCategory],
      { assignedTo: assignedTo || null, isForce },
      (data) => {
        setLead(() => ({ ...lead, assignedTo, status: data.status }));
        toast.success(`Demande assignée ${isForce ? "(forçage)" : ""}`);
      },
      (err) => {
        if (err.assignedToExists) {
          if (
            window.confirm(
              `Cette demande est déjà assignée à \n${err.assignedToExists}, \nvoulez-vous tout de même la réassigner ?`
            )
          ) {
            onUpdateAssignTo(assignedTo, true);
          } else {
            setLead(oldLead);
          }
        } else {
          setLead(oldLead);
        }
      },
      () => setIsLeadLoading(false)
    );
  };
  const onUpdateStatus = (status, reason = {}, overrideLead = null) => {
    setIsLeadLoading(true);
    const leadRef = overrideLead || lead;
    const oldLead = { ...leadRef };

    setLead({ ...leadRef, status, ...reason });

    const urlByProductCategory = {
      PRO: `leads/${leadRef.id}/status`,
      DOMI: `quotes/${leadRef.id}/status`,
    };
    dataService.patch(
      urlByProductCategory[productCategory],
      { status, ...reason },
      (data) => {
        setLead({
          ...leadRef,
          status: data.status,
          cancelReason: data.cancelReason || null,
          cancelText: data.cancelText || null,
          assignedTo: data.assignedTo,
          assignedAt: data.assignedAt,
          updatedAt: data.updatedAt,
        });
        toast.success("Statut de la demande modifié");
      },
      () => setLead(oldLead),
      () => setIsLeadLoading(false)
    );
  };

  const getLeadMRPFiles = () =>
    new Promise((resolve) => {
      dataService.getMany(
        [
          {
            type: "GET",
            path: `quotations/${lead.quotationId}/files-by-file-type/PROPO_ASSU_MRP_PERCEVAL`,
            name: "MRP_PERCEVAL",
          },
          {
            type: "GET",
            path: `quotations/${lead.quotationId}/files-by-file-type/PROPO_ASSU_MRP_PC`,
            name: "MRP_PC",
          },
        ],
        (datas) => resolve([datas.MRP_PERCEVAL, datas.MRP_PC]),
        () => resolve([])
      );
    });
  const getLeadPJFiles = () =>
    new Promise((resolve) => {
      dataService.getMany(
        [
          {
            type: "GET",
            path: `quotations/${lead.quotationId}/files-by-file-type/PROPO_ASSU_PJ_PERCEVAL`,
            name: "PJ_PERCEVAL",
          },
        ],
        (datas) => resolve([datas.PJ_PERCEVAL]),
        () => resolve([])
      );
    });
  const getLeadDOMIFiles = () =>
    new Promise((resolve) => {
      dataService.getMany(
        [
          {
            type: "GET",
            path: `quotes/${lead.id}/files`,
            name: "DOMI_PERCEVAL",
          },
        ],
        (datas) => resolve(datas.DOMI_PERCEVAL || []),
        () => resolve([])
      );
    });

  return {
    lead,
    isLeadLoading,
    isSearchingFiles,
    isFilesLoaded,
    actions: {
      initLead,
      chargerFichiers,
      ajouterMedia,
      onUpdateTasks,
      onUpdateTask,
      onAddTask,
      onDeleteTask,
      onUpdateComments,
      onUpdateComment,
      onAddComment,
      onDeleteComment,
      onUpdateAssignTo,
      onUpdateStatus,
    },
  };
};

export default useLeadItem;
